/**
 * Voice Script — Auto-scrolling voice comm log
 */

import { useRef, useEffect } from 'react'
import type { VoiceCommEntry } from '../../../../types'
import { formatSimTime } from '../../../../services/aarEngine'

interface VoiceScriptProps {
  voiceLog: VoiceCommEntry[]
}

const TYPE_COLORS: Record<VoiceCommEntry['type'], string> = {
  CHECK_IN: 'text-blue-400',
  CLEARED: 'text-emerald-400',
  COMPLETE: 'text-emerald-400',
  EMERGENCY: 'text-red-400',
  CONTINGENCY: 'text-amber-400',
  INFO: 'text-term-textDim',
}

const TYPE_BG: Record<VoiceCommEntry['type'], string> = {
  CHECK_IN: 'border-l-blue-500/40',
  CLEARED: 'border-l-emerald-500/40',
  COMPLETE: 'border-l-emerald-500/40',
  EMERGENCY: 'border-l-red-500/40 bg-red-500/5',
  CONTINGENCY: 'border-l-amber-500/40 bg-amber-500/5',
  INFO: 'border-l-gray-500/40',
}

export function VoiceScript({ voiceLog }: VoiceScriptProps) {
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [voiceLog.length])

  return (
    <div className="term-box p-3 flex flex-col h-full">
      <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">Voice Comms</div>

      <div className="flex-1 overflow-auto space-y-1 min-h-0">
        {voiceLog.length === 0 && (
          <div className="text-[10px] text-term-textDim italic">No voice comms yet...</div>
        )}
        {voiceLog.map((entry, i) => (
          <div
            key={i}
            className={`border-l-2 pl-2 py-0.5 ${TYPE_BG[entry.type]}`}
          >
            <div className="flex items-center gap-2">
              <span className="text-[9px] text-term-textDim tabular-nums shrink-0">
                {formatSimTime(entry.simTime)}
              </span>
              <span className={`text-[9px] font-bold ${TYPE_COLORS[entry.type]}`}>
                {entry.from}
              </span>
              <span className="text-[9px] text-term-textDim">→</span>
              <span className="text-[9px] text-term-textDim">{entry.to}</span>
            </div>
            <div className={`text-[10px] ${TYPE_COLORS[entry.type]} leading-snug`}>
              "{entry.message}"
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  )
}
